import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import type { AstrologerProfile, AstrologerClient, AstrologerAnalysis } from '../../../types/professional';

export function AdminAstrologerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [ap, setAp]             = useState<AstrologerProfile | null>(null);
  const [clients, setClients]   = useState<AstrologerClient[]>([]);
  const [analyses, setAnalyses] = useState<AstrologerAnalysis[]>([]);
  const [tab, setTab]           = useState<'overview' | 'clients' | 'analyses'>('overview');
  const [loading, setLoading]   = useState(true);
  const [suspending, setSuspending] = useState(false);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      supabase.from('astrologer_profiles').select('*').eq('id', id).single(),
      supabase.from('astrologer_clients').select('*').eq('astrologer_id', id).order('name'),
      supabase.from('astrologer_client_analyses').select('*').eq('astrologer_id', id).order('created_at', { ascending: false }).limit(100),
    ]).then(([{ data: p }, { data: c }, { data: a }]) => {
      setAp(p as AstrologerProfile);
      setClients((c ?? []) as AstrologerClient[]);
      setAnalyses((a ?? []) as AstrologerAnalysis[]);
      setLoading(false);
    });
  }, [id]);

  async function toggleSuspend() {
    if (!ap) return;
    setSuspending(true);
    const isSuspended = !!ap.suspended_at;
    const { data } = await supabase
      .from('astrologer_profiles')
      .update(isSuspended
        ? { suspended_at: null, suspended_reason: null }
        : { suspended_at: new Date().toISOString(), suspended_reason: 'Suspenso pelo admin' }
      )
      .eq('id', ap.id)
      .select()
      .single();
    if (data) setAp(data as AstrologerProfile);
    setSuspending(false);
  }

  if (loading) return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Carregando…</div>;
  if (!ap) return <div style={{ padding: '2rem', color: '#f87171' }}>Astrólogo não encontrado.</div>;

  const cardStyle: React.CSSProperties = { background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '1.25rem' };
  const tabBtn = (t: typeof tab, label: string) => (
    <button
      onClick={() => setTab(t)}
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem 1rem', borderBottom: tab === t ? '2px solid var(--petrol)' : '2px solid transparent', color: tab === t ? 'var(--petrol)' : 'var(--text-muted)', fontWeight: 600, fontSize: 'var(--text-label)' }}
    >{label}</button>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin-ap/astrologos" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: 'var(--text-caption)' }}>← Astrólogos</Link>
        <h1 style={{ margin: 0, fontSize: 'clamp(1.1rem,3vw,1.4rem)', color: 'var(--text-primary)', fontFamily: 'var(--font-display)', flex: 1 }}>
          {ap.professional_name}
          {ap.suspended_at && <span style={{ marginLeft: '0.75rem', fontSize: '12px', color: '#f87171', background: 'rgba(248,113,113,0.1)', padding: '3px 8px', borderRadius: '4px' }}>SUSPENSO</span>}
        </h1>
        <button
          className="btn-secondary"
          style={{ fontSize: '12px', color: ap.suspended_at ? '#4ade80' : '#f87171', borderColor: ap.suspended_at ? '#4ade80' : '#f87171' }}
          onClick={toggleSuspend}
          disabled={suspending}
        >
          {suspending ? '…' : ap.suspended_at ? 'Reativar' : 'Suspender'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)', marginBottom: '1.25rem' }}>
        {tabBtn('overview',  'Visão geral')}
        {tabBtn('clients',   `Clientes (${clients.length})`)}
        {tabBtn('analyses',  `Análises (${analyses.length})`)}
      </div>

      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: '0.75rem' }}>
            {[
              { label: 'Clientes',   value: ap.total_clients },
              { label: 'Análises',   value: ap.total_analyses },
              { label: 'Experiência', value: ap.years_experience != null ? `${ap.years_experience} anos` : '-' },
            ].map(m => (
              <div key={m.label} style={cardStyle}>
                <p style={{ margin: '0 0 0.25rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{m.label}</p>
                <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: 'var(--petrol)', fontFamily: 'var(--font-display)' }}>{m.value}</p>
              </div>
            ))}
          </div>

          {/* Profile Info */}
          <div style={cardStyle}>
            <p style={{ margin: '0 0 0.75rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>Informações do perfil</p>
            <div style={{ display: 'grid', gap: '0.4rem', fontSize: 'var(--text-caption)' }}>
              {[
                ['Título', ap.title],
                ['E-mail', ap.contact_email],
                ['WhatsApp', ap.whatsapp],
                ['Website', ap.website],
                ['Instagram', ap.instagram],
                ['Slug', ap.slug ? `@${ap.slug}` : null],
                ['Perfil público', ap.is_public ? 'Sim' : 'Não'],
                ['Wizard completo', ap.wizard_completed ? 'Sim' : 'Não'],
                ['Criado em', new Date(ap.created_at).toLocaleString('pt-BR')],
              ].map(([label, value]) => value ? (
                <div key={String(label)} style={{ display: 'flex', gap: '0.5rem' }}>
                  <span style={{ color: 'var(--text-muted)', minWidth: '120px' }}>{label}:</span>
                  <span style={{ color: 'var(--text-primary)' }}>{value}</span>
                </div>
              ) : null)}
            </div>
          </div>

          {ap.bio && (
            <div style={cardStyle}>
              <p style={{ margin: '0 0 0.5rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>Bio</p>
              <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{ap.bio}</p>
            </div>
          )}

          {ap.specialties && ap.specialties.length > 0 && (
            <div style={cardStyle}>
              <p style={{ margin: '0 0 0.5rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>Especialidades</p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {ap.specialties.map(s => (
                  <span key={s} style={{ background: 'var(--bg-base)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', color: 'var(--petrol)' }}>{s}</span>
                ))}
              </div>
            </div>
          )}

          {ap.suspended_at && (
            <div style={{ ...cardStyle, borderColor: '#f87171', background: 'rgba(248,113,113,0.05)' }}>
              <p style={{ margin: '0 0 0.3rem', fontWeight: 600, color: '#f87171', fontSize: 'var(--text-label)' }}>Conta suspensa</p>
              <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                Em {new Date(ap.suspended_at).toLocaleString('pt-BR')}
                {ap.suspended_reason && ` · ${ap.suspended_reason}`}
              </p>
            </div>
          )}
        </div>
      )}

      {tab === 'clients' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {clients.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>Nenhum cliente cadastrado.</div>
          ) : clients.map(c => (
            <div key={c.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 0.2rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>{c.name}</p>
                <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                  {new Date(c.birth_date + 'T00:00:00').toLocaleDateString('pt-BR')}
                  {c.birth_city && ` · ${c.birth_city}`}
                  {c.natal_chart_json?.sun_sign && ` · Sol em ${c.natal_chart_json.sun_sign}`}
                </p>
              </div>
              {!c.is_active && <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>inativo</span>}
            </div>
          ))}
        </div>
      )}

      {tab === 'analyses' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {analyses.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>Nenhuma análise encontrada.</div>
          ) : analyses.map(a => (
            <div key={a.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 0.2rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>{a.service_name}</p>
                <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                  {new Date(a.created_at).toLocaleString('pt-BR')}
                  {a.tokens_used ? ` · ${a.tokens_used} tokens` : ''}
                  {a.model_used ? ` · ${a.model_used}` : ''}
                </p>
              </div>
              <span style={{ fontSize: 'var(--text-caption)', color: a.status === 'completed' ? '#4ade80' : a.status === 'error' ? '#f87171' : '#fbbf24', flexShrink: 0 }}>
                {a.status === 'completed' ? '✅' : a.status === 'error' ? '❌' : '⏳'} {a.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
